import { FORMAT_PRESET_DIMENSIONS } from '@pulsify/shared/dto'

const COVER = FORMAT_PRESET_DIMENSIONS['pulsify-cover']

export const COVER_ASPECT = COVER.width / COVER.height
export const COVER_MAX_ZOOM = 3
const COVER_OUTPUT_WIDTH = 2048

interface Size {
  width: number
  height: number
}

/** How far in a cover is zoomed, and where its section sits in the room left around it (0 to 1). */
export interface CoverCrop {
  zoom: number
  x: number
  y: number
}

export const INITIAL_COVER_CROP: CoverCrop = { zoom: 1, x: 0.5, y: 0.5 }

/**
 * The frames the app shows an event cover in, on a common phone (393 points wide, a large screen).
 * It fills each frame from the middle, so they keep less of the sides than the cover format.
 * Kept in step with EventCard, DetailHeroSection and DmShareCardBanner in apps/app.
 */
export const APP_COVER_FRAMES = {
  feed: { width: 357, height: 260 },
  event: { width: 393, height: 340 },
  chat: { width: 280, height: 120 },
} as const

export type AppCoverFrame = keyof typeof APP_COVER_FRAMES

/** Where a cover-format picture sits inside an app frame it fills from the middle, in percent. */
export function coverInFrame(frame: AppCoverFrame) {
  const { width, height } = APP_COVER_FRAMES[frame]
  const aspect = width / height
  if (aspect < COVER_ASPECT) {
    const share = (COVER_ASPECT / aspect) * 100
    return { width: share, height: 100, left: (100 - share) / 2, top: 0 }
  }
  const share = (aspect / COVER_ASPECT) * 100
  return { width: 100, height: share, left: 0, top: (100 - share) / 2 }
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/** The section of an image a crop keeps, in image pixels. */
export function coverCropBox(image: Size, crop: CoverCrop) {
  const width = Math.min(image.width, image.height * COVER_ASPECT) / crop.zoom
  const height = width / COVER_ASPECT
  return {
    left: (image.width - width) * crop.x,
    top: (image.height - height) * crop.y,
    width,
    height,
  }
}

/** Moves the picture under the frame by a share of the frame's width and height. */
export function dragCoverCrop(
  image: Size,
  crop: CoverCrop,
  shareX: number,
  shareY: number,
): CoverCrop {
  const box = coverCropBox(image, crop)
  const roomX = image.width - box.width
  const roomY = image.height - box.height
  return {
    ...crop,
    x: roomX > 0 ? clamp(crop.x - (shareX * box.width) / roomX) : crop.x,
    y: roomY > 0 ? clamp(crop.y - (shareY * box.height) / roomY) : crop.y,
  }
}

/** The pixel size to save a section at: never enlarged, capped at the cover output width. */
export function coverOutputSize(box: Size): Size {
  const scale = Math.min(1, COVER_OUTPUT_WIDTH / box.width)
  return { width: Math.round(box.width * scale), height: Math.round(box.height * scale) }
}
